import { Payment, type Restrictions } from 'payments-lib-types';
import * as R from 'ramda';
import { lensPath, min } from 'ramda';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import type { User } from '@/api/auth';
import type { Currency } from '@/api/common';
import { CLIENT_ENVS } from '@/helpers/constants';
import { amountWithCurrencySymbol, currencyFlags } from '@/helpers/currency';
import { ThemeMode } from '@/helpers/enums';
import { fetchPaymentMethods } from '@/helpers/payments';
import { unmaskAmount } from '@/helpers/validators';
import { getCurrencyCentsAndCode } from '@/helpers/wallet';
import type { PaymentMethod } from '../depositStore';
import { createSelectors } from '../helpers';
import { mapCurrencyToMaxAmount } from './helpers';

export interface WithdrawStorageState {
  activeThemeMode: ThemeMode | undefined;
  paymentMethods: PaymentMethod[] | undefined;
  paymentMethodsPending: boolean;
  currentPaymentMethod: PaymentMethod | null;
  currentAmount: number | string;
  currentAmountWithCurrency: number | string;
  defaultAmountRestrictions: Restrictions | undefined;
  previouslyUsedPaymentMethods: PaymentMethod[] | undefined;
  fetchPaymentMethods: (currency: string | undefined) => void;
  setCurrentAmount: (
    user: User | undefined,
    currencies: Currency[],
    amount: number | string
  ) => void;
  setDefaultAmountRestrictions: (
    user: User | undefined,
    countryCode: string
  ) => void;
}

let fetchMethodsRequestId = 0;

const useWithdrawStoreBase = create<WithdrawStorageState>()(
  devtools(
    subscribeWithSelector((set, get) => ({
      activeThemeMode: ThemeMode.Light,
      paymentMethods: undefined,
      paymentMethodsPending: true,
      currentPaymentMethod: null,
      currentAmount: '',
      currentAmountWithCurrency: '',
      defaultAmountRestrictions: undefined,
      previouslyUsedPaymentMethods: undefined,
      fetchPaymentMethods: async (currency) => {
        const requestId = ++fetchMethodsRequestId;
        set({ paymentMethodsPending: true, paymentMethods: undefined });

        if (!currency) {
          set({ paymentMethodsPending: false });
          return;
        }

        try {
          const { data } = await fetchPaymentMethods({
            currency,
            paymentAction: Payment.Action.Cashout,
          });

          if (requestId !== fetchMethodsRequestId) return;

          const paymentMethods = data
            .map((paymentMethod) => ({
              ...paymentMethod,
              internalId: paymentMethod.id,
            }))
            .map((paymentMethod) => {
              const restrictions = paymentMethod.termsOfService.restrictions;
              const maxAmountValue = (() => {
                const maxAmount =
                  mapCurrencyToMaxAmount[restrictions.amountCurrencyCode];
                if (maxAmount && restrictions.maxAmountValue) {
                  return min(maxAmount, restrictions.maxAmountValue);
                }
                return null;
              })();
              return R.set(
                lensPath(['termsOfService', 'restrictions', 'maxAmountValue']),
                maxAmountValue,
                paymentMethod
              );
            });
          set({ paymentMethods, paymentMethodsPending: false });
        } catch (error) {
          if (requestId !== fetchMethodsRequestId) return;
          console.log(error);
          set({ paymentMethodsPending: false });
        }
      },
      setCurrentAmount: (user, currencies, currentAmount) => {
        const currencyConversion = (amount: number, currency: string) =>
          getCurrencyCentsAndCode(amount, currency, currencies).currency;

        const currentAmountWithCurrency = (() => {
          if (!user?.currency) return 0;

          const amount = unmaskAmount(currentAmount);
          const currency = currencyConversion(amount, user.currency);

          return amountWithCurrencySymbol(currency, currentAmount);
        })();
        set({ currentAmount, currentAmountWithCurrency });
      },
      setDefaultAmountRestrictions: (user, countryCode) => {
        const { currentPaymentMethod } = get();

        if (!(currentPaymentMethod && countryCode)) return;

        const restrictions = currentPaymentMethod.termsOfService.restrictions;

        const defaultAmountRestrictions = {
          minAmountValue: restrictions.minAmountValue ?? null,
          maxAmountValue: restrictions.maxAmountValue ?? null,
          amountCurrencyCode: user?.currency || currencyFlags[countryCode],
        };

        set({ defaultAmountRestrictions });
      },
    })),
    { name: 'useWithdrawStore', enabled: CLIENT_ENVS.APP_ENV === 'development' }
  )
);

export const useWithdrawStore = createSelectors(useWithdrawStoreBase);
